import { useEffect, useRef, useState } from 'react';
import { signOutUser } from '../firebase';

/** "Julian Fife" → JF; "jfife" → JF as a last resort. Only used when there's no photo. */
function initials(user) {
  const source = user?.displayName || user?.email?.replace(/@.*/, '') || '';
  const parts = source.split(/[.\s_-]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
}

/**
 * Signed-in identity, behind the avatar.
 *
 * Sign out is a destructive-ish action that belongs one click away rather than sitting
 * permanently in the header — and putting the full name and email in the panel is what
 * makes it useful: on a shared machine the avatar alone doesn't tell you *which*
 * account you are about to act as.
 */
export default function UserMenu({ user }) {
  const [open, setOpen] = useState(false);
  // Google's lh3 photo URLs intermittently 403, and a broken image in the header looks
  // like a broken app. Falling back to initials keeps the control looking deliberate.
  const [photoFailed, setPhotoFailed] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Escape closes and hands focus back to the trigger, so a keyboard user isn't left
  // pointing at a panel that is no longer there. Only bound while open — otherwise it
  // would yank focus to this button from anywhere on the page.
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const firstName = user?.displayName?.split(' ')[0] ?? user?.email?.split('@')[0] ?? 'Account';

  return (
    <div className="user-menu-wrap" ref={ref}>
      {/* A disclosure, not an ARIA menu. `role="menu"` would advertise arrow-key
          navigation between menuitems to a screen reader; this panel is a block of
          identity text with one ordinary button in it, and claiming the widget without
          implementing it is worse than not claiming it. */}
      <button
        ref={buttonRef}
        type="button"
        className="user-menu-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="user-menu-panel"
        aria-label={`Account: ${user?.email ?? firstName}`}
      >
        {user?.photoURL && !photoFailed ? (
          <img
            className="user-menu-avatar"
            src={user.photoURL}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setPhotoFailed(true)}
          />
        ) : (
          <span className="user-menu-initials" aria-hidden="true">{initials(user)}</span>
        )}
        <span className="user-menu-name">{firstName}</span>
      </button>

      {open && (
        <div className="user-menu-dropdown" id="user-menu-panel">
          <p className="user-menu-fullname">{user?.displayName ?? firstName}</p>
          <p className="user-menu-email">{user?.email}</p>
          <button type="button" className="btn-quiet user-menu-signout" onClick={signOutUser}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
